import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import {
  Ruler,
  Pentagon,
  Hash,
  Type,
  ArrowRight,
  Square,
  Cloud,
  Highlighter,
  Scale,
  Undo2,
  Redo2,
  MousePointer2,
  ZoomIn,
  ZoomOut,
  Spline,
  Circle,
  Minus,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { Annotation } from "@/hooks/useDocumentAnnotations";
import { convertLinear, convertArea } from "./takeoff-helpers";
import { cn } from "@/lib/utils";

export type TakeoffTool =
  | "select"
  | "scale"
  | "measure_linear"
  | "measure_area"
  | "polyline"
  | "count"
  | "text"
  | "arrow"
  | "rectangle"
  | "cloud"
  | "highlight"
  | "ellipse";

interface TakeoffToolbarProps {
  activeTool: TakeoffTool;
  onToolChange: (tool: TakeoffTool) => void;
  activeColor: string;
  onColorChange: (color: string) => void;
  activeLineWidth: number;
  onLineWidthChange: (w: number) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  hasScale: boolean;
  annotations: Annotation[];
  scaleUnit: string;
  displayUnit: string;
  horizontal?: boolean;
  readOnly?: boolean;
}

const MEASUREMENT_TOOL_IDS: TakeoffTool[] = ["measure_linear", "measure_area", "polyline", "count"];

const TOOLS: { id: TakeoffTool; label: string; icon: any; group?: string }[] = [
  { id: "select", label: "Select (Esc)", icon: MousePointer2 },
  { id: "scale", label: "Calibrate Scale", icon: Scale, group: "divider" },
  { id: "measure_linear", label: "Linear Measure", icon: Ruler },
  { id: "polyline", label: "Polyline Measure", icon: Spline },
  { id: "measure_area", label: "Area Measure", icon: Pentagon },
  { id: "count", label: "Count", icon: Hash, group: "divider" },
  { id: "text", label: "Text (T)", icon: Type },
  { id: "arrow", label: "Arrow", icon: ArrowRight },
  { id: "rectangle", label: "Rectangle", icon: Square },
  { id: "ellipse", label: "Ellipse", icon: Circle },
  { id: "cloud", label: "Cloud", icon: Cloud },
  { id: "highlight", label: "Highlight", icon: Highlighter },
];

const COLORS = [
  "#EF4444", "#F97316", "#EAB308", "#22C55E",
  "#3B82F6", "#8B5CF6", "#EC4899", "#000000",
];

const LINE_WIDTHS = [1, 2, 3, 5, 8];

export const TakeoffToolbar = ({
  activeTool,
  onToolChange,
  activeColor,
  onColorChange,
  activeLineWidth,
  onLineWidthChange,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onZoomIn,
  onZoomOut,
  hasScale,
  annotations,
  scaleUnit,
  displayUnit,
  horizontal = false,
  readOnly = false,
}: TakeoffToolbarProps) => {
  const linearAnns = annotations.filter((a) => a.annotation_type === "measurement_linear" || a.annotation_type === "polyline");
  const areaAnns = annotations.filter((a) => a.annotation_type === "measurement_area");
  const countAnns = annotations.filter((a) => a.annotation_type === "measurement_count");

  const rawLinear = linearAnns.reduce((s, a) => s + (a.measured_value || 0), 0);
  const rawArea = areaAnns.reduce((s, a) => s + (a.measured_value || 0), 0);
  const totalCount = countAnns.length;

  // Convert to display unit
  const totalLinear = scaleUnit !== "px" ? convertLinear(rawLinear, scaleUnit, displayUnit) : rawLinear;
  const totalArea = scaleUnit !== "px" ? convertArea(rawArea, scaleUnit, displayUnit) : rawArea;
  const linUnit = scaleUnit === "px" ? "px" : displayUnit;
  const areaUnit = scaleUnit === "px" ? "px²" : `sq ${displayUnit}`;

  const hasSummary = totalLinear > 0 || totalArea > 0 || totalCount > 0;

  // In readOnly, only show select tool
  const visibleTools = readOnly ? TOOLS.filter((t) => t.id === "select") : TOOLS;

  if (horizontal) {
    // Horizontal bottom bar layout for mobile
    return (
      <TooltipProvider delayDuration={200}>
        <div className="flex items-center gap-0.5 p-1.5 bg-card border-t border-border shrink-0 overflow-x-auto scrollbar-hide">
          {visibleTools.map((tool, idx) => (
            <div key={tool.id} className="flex items-center">
              {tool.group === "divider" && idx > 0 && <Separator orientation="vertical" className="mx-0.5 h-6" />}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={activeTool === tool.id ? "default" : "ghost"}
                    size="icon"
                    className={cn("h-8 w-8 shrink-0 relative", !hasScale && MEASUREMENT_TOOL_IDS.includes(tool.id) && "opacity-40")}
                    onClick={() => onToolChange(tool.id)}
                  >
                    <tool.icon className="h-4 w-4" />
                    {tool.id === "scale" && hasScale && (
                      <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-green-500" />
                    )}
                    {tool.id === "scale" && !hasScale && (
                      <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-destructive animate-pulse" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {tool.label}{!hasScale && MEASUREMENT_TOOL_IDS.includes(tool.id) ? " (calibrate scale first)" : ""}
                </TooltipContent>
              </Tooltip>
            </div>
          ))}

          <Separator orientation="vertical" className="mx-0.5 h-6" />

          {/* Color picker — hidden in readOnly */}
          {!readOnly && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                  <div className="h-4 w-4 rounded-full border border-border" style={{ backgroundColor: activeColor }} />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-auto p-2">
                <div className="grid grid-cols-4 gap-1">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      className="h-6 w-6 rounded-full border-2 transition-all"
                      style={{
                        backgroundColor: c,
                        borderColor: activeColor === c ? "hsl(var(--primary))" : "transparent",
                      }}
                      onClick={() => onColorChange(c)}
                    />
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Line width — hidden in readOnly */}
          {!readOnly && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                  <Minus className="h-4 w-4" style={{ strokeWidth: activeLineWidth }} />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-auto p-2">
                <div className="flex gap-1">
                  {LINE_WIDTHS.map((lw) => (
                    <button
                      key={lw}
                      className="flex items-center justify-center h-7 w-7 rounded hover:bg-accent"
                      onClick={() => onLineWidthChange(lw)}
                    >
                      <div className="w-4 rounded-full bg-foreground" style={{ height: lw }} />
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {!readOnly && (
            <>
              <Separator orientation="vertical" className="mx-0.5 h-6" />
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" disabled={!canUndo} onClick={onUndo}>
                <Undo2 className="h-4 w-4" />
              </Button>
            </>
          )}

          <Separator orientation="vertical" className="mx-0.5 h-6" />

          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onZoomIn}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onZoomOut}>
            <ZoomOut className="h-4 w-4" />
          </Button>
        </div>
      </TooltipProvider>
    );
  }

  // Vertical sidebar layout (default / desktop)
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col gap-1 p-2 bg-card border-r border-border w-12 shrink-0">
        {visibleTools.map((tool, idx) => (
          <div key={tool.id}>
            {tool.group === "divider" && idx > 0 && <Separator className="my-1" />}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={activeTool === tool.id ? "default" : "ghost"}
                  size="icon"
                  className={cn("h-8 w-8 relative", !hasScale && MEASUREMENT_TOOL_IDS.includes(tool.id) && "opacity-40")}
                  onClick={() => onToolChange(tool.id)}
                >
                  <tool.icon className="h-4 w-4" />
                  {tool.id === "scale" && hasScale && (
                    <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-green-500" />
                  )}
                  {tool.id === "scale" && !hasScale && (
                    <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-destructive animate-pulse" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {tool.label}{!hasScale && MEASUREMENT_TOOL_IDS.includes(tool.id) ? " (calibrate scale first)" : ""}
              </TooltipContent>
            </Tooltip>
          </div>
        ))}

        {!readOnly && (
          <>
            <Separator className="my-1" />

            {/* Color picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <div className="h-4 w-4 rounded-full border border-border" style={{ backgroundColor: activeColor }} />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="right" className="w-auto p-2">
                <div className="grid grid-cols-4 gap-1">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      className="h-6 w-6 rounded-full border-2 transition-all"
                      style={{
                        backgroundColor: c,
                        borderColor: activeColor === c ? "hsl(var(--primary))" : "transparent",
                      }}
                      onClick={() => onColorChange(c)}
                    />
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Line width picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Minus className="h-4 w-4" style={{ strokeWidth: activeLineWidth }} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Line Thickness</TooltipContent>
                </Tooltip>
              </PopoverTrigger>
              <PopoverContent side="right" className="w-auto p-2">
                <div className="flex flex-col gap-1">
                  {LINE_WIDTHS.map((lw) => (
                    <button
                      key={lw}
                      className="flex items-center gap-2 px-2 py-1 rounded hover:bg-accent text-xs transition-colors"
                      style={{ fontWeight: activeLineWidth === lw ? 700 : 400 }}
                      onClick={() => onLineWidthChange(lw)}
                    >
                      <div
                        className="w-8 rounded-full bg-foreground"
                        style={{ height: lw }}
                      />
                      <span>{lw}px</span>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            <Separator className="my-1" />

            {/* Undo/Redo */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={!canUndo} onClick={onUndo}>
                  <Undo2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Undo (Ctrl+Z)</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={!canRedo} onClick={onRedo}>
                  <Redo2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Redo (Ctrl+Y)</TooltipContent>
            </Tooltip>
          </>
        )}

        <Separator className="my-1" />

        {/* Zoom — always visible */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onZoomIn}>
              <ZoomIn className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Zoom In</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onZoomOut}>
              <ZoomOut className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Zoom Out</TooltipContent>
        </Tooltip>

        {/* Measurement summary removed — shown in workspace header */}
      </div>
    </TooltipProvider>
  );
};
